import { useState, useCallback, useRef } from 'react';
import { X, Bug, Upload, Trash2, Image as ImageIcon, FileText } from 'lucide-react';
import type { ProjectMember } from '../../services/projectService';

// --- Priority options ---

const PRIORITY_OPTIONS = [
  { value: 'BLOCKER', label: '블로커', color: 'bg-red-100 text-red-800' },
  { value: 'CRITICAL', label: '긴급', color: 'bg-orange-100 text-orange-800' },
  { value: 'HIGH', label: '높음', color: 'bg-amber-100 text-amber-800' },
  { value: 'MEDIUM', label: '보통', color: 'bg-blue-100 text-blue-700' },
  { value: 'LOW', label: '낮음', color: 'bg-gray-100 text-gray-600' },
] as const;

// --- Types ---

interface FileItem {
  file: File;
  preview?: string; // data URL for images
}

interface Props {
  bugTitle: string;
  members: ProjectMember[];
  onSubmit: (data: { priority: string; assigneeId?: string; files: File[] }) => void;
  onSkip: () => void;
  isPending?: boolean;
}

// --- Component ---

export default function BugInfoModal({
  bugTitle,
  members,
  onSubmit,
  onSkip,
  isPending = false,
}: Props) {
  const [priority, setPriority] = useState('MEDIUM');
  const [assigneeId, setAssigneeId] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter members by search
  const filteredMembers = members.filter((m) => {
    if (!memberSearch) return true;
    const q = memberSearch.toLowerCase();
    return (
      m.user.name.toLowerCase().includes(q) || m.user.email.toLowerCase().includes(q)
    );
  });

  // File handling
  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const arr = Array.from(newFiles);
    const items: FileItem[] = arr.map((file) => {
      const item: FileItem = { file };
      if (file.type.startsWith('image/')) {
        item.preview = URL.createObjectURL(file);
      }
      return item;
    });
    setFiles((prev) => [...prev, ...items].slice(0, 5)); // max 5 files
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => {
      const removed = prev[index];
      if (removed.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Drag & Drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  // Submit
  const handleSubmit = () => {
    onSubmit({
      priority,
      assigneeId: assigneeId || undefined,
      files: files.map((f) => f.file),
    });
  };

  // Format file size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <Bug className="size-5 text-red-500" />
            <h3 className="text-base font-semibold text-gray-900">버그 정보 설정</h3>
          </div>
          <button
            onClick={onSkip}
            disabled={isPending}
            className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
          {/* Bug title (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">제목</label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 truncate">
              {bugTitle}
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              우선순위 <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {PRIORITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPriority(opt.value)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                    priority === opt.value
                      ? `${opt.color} border-current ring-1 ring-current/20`
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Assignee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">담당자</label>
            {members.length > 5 && (
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="멤버 검색..."
                className="w-full px-3 py-1.5 mb-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            )}
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white"
            >
              <option value="">미할당</option>
              {filteredMembers.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.user.name} ({m.user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              첨부파일 <span className="text-sm text-gray-400">(최대 5개)</span>
            </label>

            {/* Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-gray-50'
              }`}
            >
              <Upload className="size-5 text-gray-400" />
              <span className="text-sm text-gray-500">
                클릭하거나 파일을 드래그하세요
              </span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf,application/zip,video/*"
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  e.target.value = '';
                }}
                className="hidden"
              />
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {files.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-100 rounded-lg"
                  >
                    {/* Thumbnail or icon */}
                    {item.preview ? (
                      <img
                        src={item.preview}
                        alt={item.file.name}
                        className="w-10 h-10 rounded object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded shrink-0">
                        {item.file.type.includes('pdf') ? (
                          <FileText className="size-5 text-red-400" />
                        ) : (
                          <ImageIcon className="size-5 text-gray-400" />
                        )}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-700 truncate">{item.file.name}</div>
                      <div className="text-[11px] text-gray-400">{formatSize(item.file.size)}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(idx);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={onSkip}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            건너뛰기
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {isPending ? '생성 중...' : '생성'}
          </button>
        </div>
      </div>
    </div>
  );
}
